// 상담 채팅 실시간(STOMP/SockJS) — 고객(idpWeb) 쪽. idpAdmin의 realtime.ts와 비슷한 구조지만,
// 신원 확인 방식이 다르다: 상담사(X-Admin-Id)는 FE가 STOMP CONNECT에 직접 헤더를 실어보내는 스텁이지만,
// 고객 쪽은 C-05 §3.5 표준대로 FE가 신원을 절대 보내지 않는다 — FlowGate가 최초 접속(핸드셰이크) HTTP
// 요청에만 검증된 X-User-Id를 넣어주고, sv-support의 SupportHandshakeInterceptor가 그걸 세션에 저장해서
// STOMP 인증에 쓴다. 그래서 이 파일은 connectHeaders를 전혀 설정하지 않는다.
// 테스트 화면 전용 모듈이라 로그인 전역 상시 연결이 아니라 화면(페이지) mount/unmount에 연결을 맡긴다.
//
// 인증: 브라우저 WebSocket/SockJS는 REST처럼 Authorization 헤더를 못 실어서(브라우저 제약) ?token=
// 쿼리로 전달한다 — FlowGate의 SseTokenHeaderFilter가 /api/support/ws-support 경로에서 이걸 읽어
// Authorization: Bearer로 변환해준다. 이 화면은 한 번 열려있는 동안 오래 대기할 수 있어 토큰이
// 만료될 수 있으므로, (재)연결마다 webSocketFactory 안에서 그 시점의 최신 토큰을 다시 읽는다.
import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useAuthStore } from '@indipia/shared';

const API_BASE = import.meta.env.VITE_API_BASE || '';
const ENDPOINT = `${API_BASE}/api/support/ws-support`;

export type ConnectionState = 'connecting' | 'connected' | 'disconnected';

type Handler = (body: string) => void;
interface SubEntry { handler: Handler; sub?: StompSubscription }

let client: Client | null = null;
const subs = new Map<string, SubEntry>();
let stateListener: ((state: ConnectionState) => void) | null = null;

function setState(state: ConnectionState) {
  stateListener?.(state);
}

function resubscribeAll() {
  if (!client?.connected) return;
  subs.forEach((entry, destination) => {
    entry.sub = client!.subscribe(destination, (msg: IMessage) => entry.handler(msg.body));
  });
}

/**
 * 항상 새 연결을 만든다 — 이전 연결(끊겼거나 만료된 토큰으로 재시도만 반복 중인 것 포함)이 남아있으면
 * 먼저 정리하고 새로 붙인다. onStateChange로 연결 상태를 화면에 표시할 수 있다.
 */
export function connectSupportChat(onStateChange?: (state: ConnectionState) => void): Client {
  client?.deactivate();
  stateListener = onStateChange ?? null;
  setState('connecting');

  client = new Client({
    webSocketFactory: () => {
      const token = useAuthStore.getState().accessToken;
      const url = token ? `${ENDPOINT}?token=${encodeURIComponent(token)}` : ENDPOINT;
      return new SockJS(url);
    },
    reconnectDelay: 5000,
    onConnect: () => {
      setState('connected');
      resubscribeAll();
    },
    onWebSocketClose: () => setState('disconnected'),
    onDisconnect: () => setState('disconnected'),
  });
  client.activate();
  return client;
}

export function disconnectSupportChat() {
  subs.clear();
  stateListener = null;
  client?.deactivate();
  client = null;
}

export function subscribeSupportChatTopic(destination: string, handler: Handler): () => void {
  const entry: SubEntry = { handler };
  subs.set(destination, entry);
  if (client?.connected) {
    entry.sub = client.subscribe(destination, (msg) => handler(msg.body));
  }
  return () => {
    subs.get(destination)?.sub?.unsubscribe();
    subs.delete(destination);
  };
}

/** 채팅 전송 — 페이로드는 JSON이 아니라 순수 텍스트(백엔드 SupportMessageHandler와 동일 계약). */
export function sendSupportChatMessage(caseId: string, content: string) {
  if (!client?.connected) return false;
  client.publish({ destination: `/app/support.case.${caseId}.send`, body: content });
  return true;
}
